import React, { useEffect, useRef, useState } from 'react';
import { CloseButton, Modal, Textarea } from '@mantine/core';
import {
  ASSISTANT_HISTORY_MAX_TURNS,
  ASSISTANT_HISTORY_TURN_MAX_LENGTH,
  ASSISTANT_QUESTION_MAX_LENGTH,
  ASSISTANT_QUESTION_MIN_LENGTH,
  AssistantHistoryTurn,
} from 'shared-models/browser';
import { Button } from '../Button';
import { toHebrewApiErrorMessage } from '../../services/index/index.service';
import { AssistantFeedbackModal } from './AssistantFeedbackModal';
import {
  ChevronLeftIcon,
  CloseIcon,
  IconProps,
  InfoIcon,
  MessageIcon,
  PencilIcon,
  SendIcon,
  SparklesIcon,
} from './assistant-panel-icons';
import { useAssistantAutoScroll } from './useAssistantAutoScroll';
import { useVisualViewportHeight } from './useVisualViewportHeight';
import classes from './assistant-panel.module.scss';

type Message = { role: 'user' | 'assistant'; content: string };

export type AssistantSuggestion = { text: string; Icon: React.FC<IconProps> };

// After a failed turn the capped tail can open on an assistant turn, which the API rejects.
export const historyForRequest = (messages: Message[]): AssistantHistoryTurn[] => {
  const tail = messages.slice(-ASSISTANT_HISTORY_MAX_TURNS);
  const firstUser = tail.findIndex((message) => message.role === 'user');
  return (firstUser < 0 ? [] : tail.slice(firstUser))
    .map((message) => ({ role: message.role, text: message.content.slice(0, ASSISTANT_HISTORY_TURN_MAX_LENGTH) }));
};

const REQUEST_TIMEOUT_MS = 45_000;
const TIMEOUT_REASON = 'assistant-timeout';
const TIMEOUT_MESSAGE = 'הבדיקה ארכה יותר מדי זמן. נסו שאלה ממוקדת יותר.';
const QUICK_QUESTIONS_LABEL = 'שאלות מהירות';
const DISCLAIMER = 'העוזר מבוסס על AI ויכול לטעות';
const FEEDBACK_LABEL = 'הערה לשיפור?';
const EMPTY_QUESTION_ERROR = 'כתבו שאלה קודם';
const LOADING_LABEL = 'בודק את הנתונים, זה יכול לקחת עד דקה...';
const KEYBOARD_SETTLE_MS = 300;
const KEYBOARD_TOP_RATIO = 0.5;
const KEYBOARD_SAFE_HEIGHT_RATIO = 0.45;
const KEYBOARD_SAFE_MIN_HEIGHT_PX = 220;
const KEYBOARD_SAFE_GAP_PX = 12;

export type KeyboardSafeBox = { marginTop: number; maxHeight: number };

// Shrinks the panel in place above the keyboard; the top only moves if the min box cannot fit.
export const keyboardSafeBox = (
  contentTop: number,
  innerTop: number,
  innerPaddingTop: number,
  innerHeight: number,
): KeyboardSafeBox => {
  const keyboardTop = innerHeight * KEYBOARD_TOP_RATIO - KEYBOARD_SAFE_GAP_PX;
  const fitted = Math.min(innerHeight * KEYBOARD_SAFE_HEIGHT_RATIO, keyboardTop - contentTop);
  const maxHeight = Math.max(fitted, KEYBOARD_SAFE_MIN_HEIGHT_PX);
  const top = Math.min(contentTop, keyboardTop - maxHeight);
  return { marginTop: Math.max(0, top - innerTop - innerPaddingTop), maxHeight };
};

export type AssistantChatPanelProps = {
  testIdPrefix: string;
  title: string;
  subtitle: string;
  statusLabel: string;
  placeholder: string;
  suggestions: AssistantSuggestion[];
  opened: boolean;
  onClose: () => void;
  ask: (question: string, history: AssistantHistoryTurn[], signal: AbortSignal) => Promise<string>;
  sendFeedback: (message: string) => Promise<void>;
  canAsk?: boolean;
  zIndex?: number;
  feedbackZIndex?: number;
};

export const AssistantChatPanel: React.FC<AssistantChatPanelProps> = ({
  testIdPrefix,
  title,
  subtitle,
  statusLabel,
  placeholder,
  suggestions,
  opened,
  onClose,
  ask: askApi,
  sendFeedback,
  canAsk = true,
  zIndex,
  feedbackZIndex,
}) => {
  const panelTestId = `${testIdPrefix}-panel`;
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputError, setInputError] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const inFlightRef = useRef(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const settleTimeoutRef = useRef(0);
  const keyboardOpenRef = useRef(false);
  const [keyboardSafe, setKeyboardSafe] = useState<KeyboardSafeBox | null>(null);
  const { height: viewportHeight, offsetTop: viewportOffsetTop, isKeyboardOpen } = useVisualViewportHeight(opened);
  const { messagesRef, handleMessagesScroll } = useAssistantAutoScroll(
    opened,
    `${messages.length}|${loading}|${viewportHeight}|${keyboardSafe?.maxHeight ?? 0}`,
  );

  useEffect(() => { keyboardOpenRef.current = isKeyboardOpen; }, [isKeyboardOpen]);

  useEffect(() => () => {
    abortRef.current?.abort();
    window.clearTimeout(settleTimeoutRef.current);
  }, []);

  // A resize arriving after the settle deadline proves an adjustResize shell: drop the pan box.
  useEffect(() => {
    if (!isKeyboardOpen || !keyboardSafe) return;
    setKeyboardSafe(null);
    inputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
  }, [isKeyboardOpen, keyboardSafe]);

  // The panel stays mounted after close, so a close without blur must clear the pending settle.
  useEffect(() => {
    if (opened) return;
    window.clearTimeout(settleTimeoutRef.current);
    setKeyboardSafe(null);
  }, [opened]);

  // No resize by the settle deadline means an adjustPan shell, which gives no keyboard signal.
  const handleInputFocus = () => {
    window.clearTimeout(settleTimeoutRef.current);
    settleTimeoutRef.current = window.setTimeout(() => {
      const resized = keyboardOpenRef.current;
      const content = panelRef.current?.closest(`.${classes.modalContent}`) ?? null;
      const inner = content?.parentElement ?? null;
      const contentRect = content?.getBoundingClientRect();
      const innerRect = inner?.getBoundingClientRect();
      if (resized) {
        inputRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        return;
      }
      if (!contentRect || !innerRect || !inner) return;
      const paddingTop = parseFloat(window.getComputedStyle(inner).paddingTop) || 0;
      setKeyboardSafe(keyboardSafeBox(contentRect.top, innerRect.top, paddingTop, window.innerHeight));
    }, KEYBOARD_SETTLE_MS);
  };

  const handleInputBlur = () => {
    window.clearTimeout(settleTimeoutRef.current);
    setKeyboardSafe(null);
  };

  // Drops the running question; its own cleanup then sees a foreign controller and stands down.
  const abortInFlight = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    inFlightRef.current = false;
    setLoading(false);
  };

  // Any close is a full reset: the conversation is dropped and an in-flight question is aborted.
  const reset = () => {
    abortInFlight();
    setMessages([]);
    setQuestion('');
    setError(null);
    setInputError(false);
    onClose();
  };

  // Editing turn N aborts a running answer, restores the text and drops that turn and later ones.
  const editMessage = (index: number) => {
    abortInFlight();
    setQuestion(messages[index]?.content ?? '');
    setMessages((current) => current.slice(0, index));
    setError(null);
    setInputError(false);
  };

  const ask = async (nextQuestion = question) => {
    const trimmed = nextQuestion.trim();
    if (trimmed.length < ASSISTANT_QUESTION_MIN_LENGTH) {
      setInputError(true);
      return;
    }
    if (inFlightRef.current || !canAsk) return;
    inFlightRef.current = true;
    setQuestion('');
    setError(null);
    setInputError(false);
    const history = historyForRequest(messages);
    setMessages((current) => [...current, { role: 'user', content: trimmed }]);
    setLoading(true);
    const controller = new AbortController();
    abortRef.current = controller;
    const timeoutId = window.setTimeout(() => controller.abort(TIMEOUT_REASON), REQUEST_TIMEOUT_MS);
    try {
      const answer = await askApi(trimmed, history, controller.signal);
      if (abortRef.current === controller) {
        setMessages((current) => [...current, { role: 'assistant', content: answer }]);
      }
    } catch (caught: unknown) {
      if (controller.signal.reason === TIMEOUT_REASON) {
        setError(TIMEOUT_MESSAGE);
      } else if (!controller.signal.aborted) {
        setError(caught instanceof Error ? toHebrewApiErrorMessage(caught.message) : 'אירעה שגיאה. נסו שוב.');
      }
    } finally {
      window.clearTimeout(timeoutId);
      // A reset or an edit already took over: its fresh state must not be undone by this cleanup.
      if (abortRef.current === controller) {
        abortRef.current = null;
        inFlightRef.current = false;
        setLoading(false);
      }
    }
  };

  const isEmpty = messages.length === 0 && !loading;

  const handleQuestionChange = (value: string) => {
    setQuestion(value);
    setInputError(false);
  };

  // Enter sends, Shift+Enter keeps the multi-line newline the textarea allows.
  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    void ask();
  };

  return (
    <>
      <Modal
        opened={opened}
        onClose={reset}
        withCloseButton={false}
        padding={0}
        size="lg"
        centered
        dir="rtl"
        zIndex={zIndex}
        classNames={{
          inner: classes.modalInner,
          content: `${classes.modalContent}${keyboardSafe ? ` ${classes.modalContentKeyboardSafe}` : ''}`,
          body: classes.modalBody,
        }}
        styles={{
          root: {
            '--assistant-vv-height': `${viewportHeight}px`,
            '--assistant-vv-offset': `${viewportOffsetTop}px`,
          } as React.CSSProperties,
          content: keyboardSafe
            ? { marginTop: `${keyboardSafe.marginTop}px`, maxHeight: `${keyboardSafe.maxHeight}px` }
            : undefined,
        }}
      >
        <div ref={panelRef} className={classes.panel} data-testid={panelTestId}>
          <header className={classes.header}>
            <div className={classes.headerRow}>
              <span className={classes.iconTile} aria-hidden="true"><SparklesIcon className={classes.iconTileGlyph} /></span>
              <div className={classes.headerText}>
                <div className={classes.headerTitleRow}>
                  <h2 className={classes.title}>{title}</h2>
                  <span className={classes.statusPill}>
                    <span className={classes.statusDot} aria-hidden="true" />
                    <span className={classes.statusLabel}>{statusLabel}</span>
                  </span>
                </div>
                <p className={classes.subtitle}>{subtitle}</p>
              </div>
              <CloseButton
                className={classes.close}
                icon={<CloseIcon className={classes.closeIcon} />}
                onClick={reset}
                aria-label="סגירה"
                data-testid={`${panelTestId}-close`}
              />
            </div>
          </header>
          <div className={classes.body}>
            {isEmpty ? (
              <div className={classes.questions} data-testid={`${panelTestId}-empty`}>
                <p className={classes.quickLabel}>{QUICK_QUESTIONS_LABEL}</p>
                <div className={classes.suggestions}>
                  {suggestions.map((suggestion, index) => (
                    <Button
                      key={suggestion.text}
                      variant="cancel"
                      size="sm"
                      className={classes.suggestion}
                      dataTestId={`${panelTestId}-suggestion-${index}`}
                      onClick={() => void ask(suggestion.text)}
                    >
                      <span className={classes.suggestionTile} aria-hidden="true">
                        <suggestion.Icon className={classes.suggestionIcon} />
                      </span>
                      <span className={classes.suggestionText}>{suggestion.text}</span>
                      <ChevronLeftIcon className={classes.suggestionChevron} />
                    </Button>
                  ))}
                </div>
              </div>
            ) : null}
            <div
              ref={messagesRef}
              className={classes.messages}
              aria-live="polite"
              onScroll={handleMessagesScroll}
              data-testid={`${panelTestId}-messages`}
            >
              {messages.map((message, index) => (message.role === 'assistant' ? (
                <div
                  key={`${message.role}-${index}`}
                  className={classes.assistantMessage}
                  data-testid={`${panelTestId}-message-${index}`}
                >
                  {message.content}
                </div>
              ) : (
                <div key={`${message.role}-${index}`} className={classes.userRow}>
                  <Button
                    variant="icon"
                    className={classes.editButton}
                    ariaLabel="עריכת השאלה"
                    dataTestId={`${panelTestId}-message-edit-${index}`}
                    onClick={() => editMessage(index)}
                  >
                    <PencilIcon className={classes.editIcon} />
                  </Button>
                  <div className={classes.userMessage} data-testid={`${panelTestId}-message-${index}`}>
                    {message.content}
                  </div>
                </div>
              )))}
              {loading ? <div className={classes.assistantMessage} data-testid={`${panelTestId}-loading`}>{LOADING_LABEL}</div> : null}
            </div>
            {error ? <div className={classes.error} data-testid={`${panelTestId}-error`}>{error}</div> : null}
            <div className={classes.inputArea}>
              <div className={classes.composer} data-testid={`${panelTestId}-composer`}>
                <Textarea
                  ref={inputRef}
                  value={question}
                  onChange={(event) => handleQuestionChange(event.currentTarget.value)}
                  placeholder={placeholder}
                  maxLength={ASSISTANT_QUESTION_MAX_LENGTH}
                  rows={1}
                  onKeyDown={handleInputKeyDown}
                  onFocus={handleInputFocus}
                  onBlur={handleInputBlur}
                  className={classes.input}
                  classNames={{ wrapper: classes.inputWrapper }}
                  data-testid={`${panelTestId}-input`}
                />
                <Button
                  variant="confirm"
                  loading={loading}
                  loadingLabel=""
                  className={classes.sendButton}
                  ariaLabel="שלח"
                  dataTestId={`${panelTestId}-send`}
                  onClick={() => void ask()}
                >
                  <SendIcon className={classes.sendIcon} />
                </Button>
              </div>
              {inputError ? (
                <p className={classes.inputError} data-testid={`${panelTestId}-input-error`}>{EMPTY_QUESTION_ERROR}</p>
              ) : null}
              <div className={classes.footer}>
                <span className={classes.disclaimer} data-testid={`${panelTestId}-disclaimer`}>
                  <InfoIcon className={classes.disclaimerIcon} />
                  {DISCLAIMER}
                </span>
                <Button
                  variant="cancel"
                  size="sm"
                  className={classes.feedbackButton}
                  dataTestId={`${panelTestId}-feedback-open`}
                  onClick={() => setIsFeedbackOpen(true)}
                >
                  <MessageIcon className={classes.feedbackIcon} />
                  <span>{FEEDBACK_LABEL}</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <AssistantFeedbackModal
        testIdPrefix={testIdPrefix}
        opened={isFeedbackOpen}
        onClose={() => setIsFeedbackOpen(false)}
        sendFeedback={sendFeedback}
        zIndex={feedbackZIndex}
      />
    </>
  );
};
